import type {
  AchMatrix,
  AiAnalysis,
  AiModelList,
  AiProviderInfo,
  AiStatus,
  AppUser,
  AuditEntry,
  AuthUser,
  BoardData,
  BoardStyle,
  CaseMember,
  CollectBundle,
  Engagement,
  Entity,
  Evidence,
  Hypothesis,
  IntegrityReport,
  MetaConfig,
  OperatePlan,
  Relation,
  Requirement,
  RequirementCoverage,
  RiskResult,
  ShareLink,
  ShareSnapshot,
  Stats,
  Target,
  TimelineEvent,
} from './domain';

const BASE = '/api';
const TOKEN_KEY = 'expotar.token';

let authToken: string | null =
  typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  if (typeof localStorage === 'undefined') return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  return authToken;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (authToken) headers.authorization = `Bearer ${authToken}`;

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
  });

  if (res.status === 401) {
    setAuthToken(null);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('expotar:unauthorized'));
  }

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    const message = (detail as { error?: string })?.error ?? `请求失败（${res.status}）`;
    const error = new Error(message) as Error & { status?: number; detail?: unknown };
    error.status = res.status;
    error.detail = detail;
    throw error;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * 页面关闭时的兜底落盘：sendBeacon 无法自定义请求头，因此用 ?token= 鉴权，
 * 走 POST /api/board/sync（服务端与 PUT 同一处理器）。
 */
export function sendBoardBeacon(
  engagementId: string,
  payload: {
    viewport?: { x: number; y: number; zoom: number };
    style?: BoardStyle;
    upserts?: unknown[];
    deletes?: string[];
  }
): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
  const token = getAuthToken();
  const url = `${BASE}/board/sync${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  const body = new Blob([JSON.stringify({ engagementId, ...payload })], { type: 'application/json' });
  try {
    return navigator.sendBeacon(url, body);
  } catch {
    return false;
  }
}

export const api = {
  // auth
  authStatus: () => request<{ needsSetup: boolean; registrationOpen: boolean }>('/auth/status'),
  login: (body: { identifier: string; password: string }) =>
    request<{ token: string; expiresAt: string; user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  register: (body: { email: string; name: string; password: string }) =>
    request<{ token: string; expiresAt: string; user: AuthUser }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<{ user: AuthUser; createdAt: string; lastLoginAt: string | null }>('/auth/me'),
  updateMe: (body: { name?: string; username?: string; avatar?: string | null }) =>
    request<{ user: AuthUser }>('/auth/me', { method: 'PATCH', body: JSON.stringify(body) }),
  changePassword: (body: { current: string; next: string }) =>
    request<{ token: string }>('/auth/password', { method: 'POST', body: JSON.stringify(body) }),

  // 账号管理（平台管理员）
  listUsers: () => request<{ items: AppUser[] }>('/users'),
  createUser: (body: { email: string; name: string; role?: string; username?: string; password: string }) =>
    request<AppUser>('/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: string, body: Record<string, unknown>) =>
    request<AppUser>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteUser: (id: string) => request<void>(`/users/${id}`, { method: 'DELETE' }),

  // AI 供应商管理（管理员）
  createAiProvider: (body: Record<string, unknown>) =>
    request<{ id: string; providers: AiProviderInfo[] }>('/ai/providers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateAiProvider: (id: string, body: Record<string, unknown>) =>
    request<{ providers: AiProviderInfo[] }>(`/ai/providers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAiProvider: (id: string) =>
    request<{ providers: AiProviderInfo[] }>(`/ai/providers/${id}`, { method: 'DELETE' }),

  // ACH 竞争性假设矩阵
  getAch: (engagementId: string) =>
    request<AchMatrix>(`/ach?engagementId=${encodeURIComponent(engagementId)}`),
  rateAch: (body: {
    engagementId: string;
    hypothesisId: string;
    evidenceId: string;
    rating: '++' | '+' | 'NA' | '-' | '--';
    note?: string;
  }) => request<{ ok: boolean }>('/ach', { method: 'PUT', body: JSON.stringify(body) }),
  clearAch: (engagementId: string, hypothesisId: string, evidenceId: string) =>
    request<void>(
      `/ach?engagementId=${encodeURIComponent(engagementId)}&hypothesisId=${encodeURIComponent(
        hypothesisId
      )}&evidenceId=${encodeURIComponent(evidenceId)}`,
      { method: 'DELETE' }
    ),
  autofillAch: (engagementId: string) =>
    request<{ written: number }>('/ach/auto', {
      method: 'POST',
      body: JSON.stringify({ engagementId }),
    }),

  // 证据链完整性
  verifyEvidenceChain: (engagementId: string, repair = false) =>
    request<IntegrityReport>(
      `/evidence/integrity?engagementId=${encodeURIComponent(engagementId)}${repair ? '&repair=1' : ''}`
    ),

  // 情报需求
  listRequirements: (engagementId: string) =>
    request<{ items: Requirement[] }>(`/requirements?engagementId=${encodeURIComponent(engagementId)}`),
  requirementCoverage: (engagementId: string) =>
    request<RequirementCoverage>(
      `/requirements/coverage?engagementId=${encodeURIComponent(engagementId)}`
    ),
  createRequirement: (body: Record<string, unknown>) =>
    request<Requirement>('/requirements', { method: 'POST', body: JSON.stringify(body) }),
  updateRequirement: (id: string, body: Record<string, unknown>) =>
    request<Requirement>(`/requirements/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteRequirement: (id: string) => request<void>(`/requirements/${id}`, { method: 'DELETE' }),

  // members
  listMembers: (engagementId: string) =>
    request<{ items: CaseMember[] }>(`/members?engagementId=${encodeURIComponent(engagementId)}`),
  addMember: (body: {
    engagementId: string;
    email: string;
    role: 'editor' | 'viewer';
    name?: string;
    username?: string;
    password?: string;
  }) =>
    request<{ items: CaseMember[] }>('/members', { method: 'POST', body: JSON.stringify(body) }),
  updateMember: (userId: string, engagementId: string, role: 'editor' | 'viewer') =>
    request<{ items: CaseMember[] }>(
      `/members/${userId}?engagementId=${encodeURIComponent(engagementId)}`,
      { method: 'PATCH', body: JSON.stringify({ role }) }
    ),
  transferOwner: (engagementId: string, userId: string) =>
    request<{ items: CaseMember[] }>('/members/transfer', {
      method: 'POST',
      body: JSON.stringify({ engagementId, userId }),
    }),
  removeMember: (userId: string, engagementId: string) =>
    request<{ items: CaseMember[] }>(
      `/members/${userId}?engagementId=${encodeURIComponent(engagementId)}`,
      { method: 'DELETE' }
    ),

  // shares
  listShares: (engagementId: string) =>
    request<{ items: ShareLink[] }>(`/shares?engagementId=${encodeURIComponent(engagementId)}`),
  createShare: (body: { engagementId: string; label?: string; expiresInDays?: number }) =>
    request<ShareLink>('/shares', { method: 'POST', body: JSON.stringify(body) }),
  revokeShare: (token: string) => request<void>(`/shares/${token}`, { method: 'DELETE' }),
  shareSnapshot: (token: string) => request<ShareSnapshot>(`/share/${token}`),

  // meta
  config: () => request<MetaConfig>('/meta/config'),
  health: () => request<{ ok: boolean; engagements: number }>('/meta/health'),
  stats: (engagementId: string) => request<Stats>(`/meta/stats?engagementId=${encodeURIComponent(engagementId)}`),
  aiStatus: () => request<AiStatus>('/ai/status'),
  aiModels: (providerId?: string | null) =>
    request<AiModelList>(`/ai/models${providerId ? `?providerId=${encodeURIComponent(providerId)}` : ''}`),

  // engagements
  listEngagements: () => request<{ items: Engagement[] }>('/engagements'),
  createEngagement: (body: Partial<Engagement> & { name: string }) =>
    request<Engagement>('/engagements', { method: 'POST', body: JSON.stringify(body) }),
  updateEngagement: (id: string, body: Partial<Engagement>) =>
    request<Engagement>(`/engagements/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEngagement: (id: string) => request<void>(`/engagements/${id}`, { method: 'DELETE' }),

  // targets
  listTargets: (engagementId: string) =>
    request<{ items: Target[] }>(`/targets?engagementId=${encodeURIComponent(engagementId)}`),
  getTarget: (id: string) =>
    request<{ target: Target; entities: Entity[]; evidence: Evidence[]; hypotheses: Hypothesis[] }>(`/targets/${id}`),
  createTarget: (body: Record<string, unknown>) =>
    request<Target>('/targets', { method: 'POST', body: JSON.stringify(body) }),
  updateTarget: (id: string, body: Record<string, unknown>) =>
    request<Target & { riskFactors?: unknown[] }>(`/targets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTarget: (id: string) => request<void>(`/targets/${id}`, { method: 'DELETE' }),
  targetRisk: (id: string) => request<RiskResult & { targetId: string }>(`/targets/${id}/risk`),

  // graph
  listEntities: (engagementId: string) =>
    request<{ items: Entity[] }>(`/entities?engagementId=${encodeURIComponent(engagementId)}`),
  createEntity: (body: Record<string, unknown>) =>
    request<Entity>('/entities', { method: 'POST', body: JSON.stringify(body) }),
  updateEntity: (id: string, body: Record<string, unknown>) =>
    request<Entity>(`/entities/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEntity: (id: string) => request<void>(`/entities/${id}`, { method: 'DELETE' }),

  listRelations: (engagementId: string) =>
    request<{ items: Relation[] }>(`/relations?engagementId=${encodeURIComponent(engagementId)}`),
  createRelation: (body: Record<string, unknown>) =>
    request<Relation>('/relations', { method: 'POST', body: JSON.stringify(body) }),
  updateRelation: (id: string, body: Record<string, unknown>) =>
    request<Relation>(`/relations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteRelation: (id: string) => request<void>(`/relations/${id}`, { method: 'DELETE' }),

  listEvidence: (engagementId: string) =>
    request<{ items: Evidence[] }>(`/evidence?engagementId=${encodeURIComponent(engagementId)}`),
  createEvidence: (body: Record<string, unknown>) =>
    request<Evidence>('/evidence', { method: 'POST', body: JSON.stringify(body) }),
  updateEvidence: (id: string, body: Record<string, unknown>) =>
    request<Evidence>(`/evidence/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteEvidence: (id: string) => request<void>(`/evidence/${id}`, { method: 'DELETE' }),

  listHypotheses: (engagementId: string) =>
    request<{ items: Hypothesis[] }>(`/hypotheses?engagementId=${encodeURIComponent(engagementId)}`),
  createHypothesis: (body: Record<string, unknown>) =>
    request<Hypothesis>('/hypotheses', { method: 'POST', body: JSON.stringify(body) }),
  updateHypothesis: (id: string, body: Record<string, unknown>) =>
    request<Hypothesis>(`/hypotheses/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteHypothesis: (id: string) => request<void>(`/hypotheses/${id}`, { method: 'DELETE' }),

  // timeline
  listTimeline: (engagementId: string) =>
    request<{ items: TimelineEvent[] }>(`/timeline?engagementId=${encodeURIComponent(engagementId)}`),
  createTimeline: (body: Record<string, unknown>) =>
    request<TimelineEvent>('/timeline', { method: 'POST', body: JSON.stringify(body) }),
  updateTimeline: (id: string, body: Record<string, unknown>) =>
    request<TimelineEvent>(`/timeline/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTimeline: (id: string) => request<void>(`/timeline/${id}`, { method: 'DELETE' }),

  // board
  getBoard: (engagementId: string) =>
    request<BoardData>(`/board?engagementId=${encodeURIComponent(engagementId)}`),
  syncBoard: (body: {
    engagementId: string;
    viewport?: { x: number; y: number; zoom: number };
    style?: BoardStyle;
    upserts?: unknown[];
    deletes?: string[];
  }) => request<{ ok: boolean }>('/board/sync', { method: 'PUT', body: JSON.stringify(body) }),

  // intel
  collect: (body: { engagementId: string; targetId?: string | null; kind: string; value: string; save?: boolean }) =>
    request<{ bundle: CollectBundle; saved: { evidence: string[]; entities: string[] } }>('/intel/collect', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  importIntel: (body: Record<string, unknown>) =>
    request<{ created: number; relations: number }>('/intel/import', { method: 'POST', body: JSON.stringify(body) }),

  // ai
  analyze: (body: {
    engagementId: string;
    targetId: string;
    focus?: string;
    saveHypotheses?: boolean;
    providerId?: string | null;
    model?: string | null;
  }) =>
    request<{ analysis: AiAnalysis; risk: RiskResult; savedHypotheses: string[] }>('/ai/analyze', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  analyzeDraft: (body: Record<string, unknown>) =>
    request<{ analysis: AiAnalysis; risk: RiskResult }>('/ai/analyze-draft', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  operate: (body: {
    engagementId: string;
    targetId?: string | null;
    instruction?: string;
    providerId?: string | null;
    model?: string | null;
  }) =>
    request<OperatePlan>('/ai/operate', { method: 'POST', body: JSON.stringify(body) }),

  // audit
  listAudit: (engagementId: string, limit = 200) =>
    request<{ items: AuditEntry[] }>(`/audit?engagementId=${encodeURIComponent(engagementId)}&limit=${limit}`),
};